import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  difference,
  filter,
  flatMap,
  includes,
  intersection,
  isEmpty,
  map,
  orderBy,
  uniqBy,
} from "lodash";
import moment from "moment";

import { ContextPageTitle } from "components/layout/PageTitle";
import AppointmentToolbar from "components/appointments/AppointmentToolbar";
import CalendarSheet from "./CalendarSheet.js";
import { useAppointments, useCalendars } from "components/appointments/api";
import { collectAppointmentsByDate } from "components/appointments/helpers";
import AppointmentsList from "components/appointments/appointmentsList";
import { useParams, useSearchParams } from "helpers/tixxt-router";
import GroupNavigation from "components/layout/GroupNavigation";
import { useSelector } from "react-redux";
import { State } from "../../@types";
import { Appointment } from "../../@types/appointments";
import useAppointmentView from "components/appointments/useAppointmentView";
import { stringify } from "query-string";
import {
  compressObjectIds,
  decompressObjectIds,
} from "helpers/objectIdCompression";

export default function AppointmentsPage({
  participating = false,
}: {
  participating?: boolean;
}) {
  const { view, setViewType } = useAppointmentView();
  const { groupSlug } = useParams();
  const [searchParams] = useSearchParams();
  const group = useSelector((state: State) =>
    groupSlug ? state.groups.bySlug?.[groupSlug] : null,
  );
  const network = useSelector((state: State) => state.network);

  const [date, setDate] = useState(
    sessionStorage.getItem("appointmentsDate") || moment().format(),
  );
  const [excludedCalendarIds, setExcludedCalendarIds] = useState<string[]>([]);

  const { isLoading, data: appointments } = useAppointments({
    date,
    view,
    participating,
  });
  const todayRef = useRef<HTMLDivElement>(null);
  const scrollToToday = () => {
    setTimeout(() => {
      if (todayRef.current) {
        todayRef.current.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    }, 100);
  };
  const { data: calendars } = useCalendars();
  let contextCalendars = !isEmpty(group)
    ? filter(calendars, (cal) => cal.parent_id === group?.id)
    : calendars;
  contextCalendars = (contextCalendars || []).concat(
    uniqBy(flatMap(appointments, "unknown_calendars"), "id"),
  );

  useEffect(() => {
    if (searchParams) {
      const compressedCalendarIds = searchParams.get("calendarIds");

      if (compressedCalendarIds && !isEmpty(compressedCalendarIds)) {
        const selectedCalendarIds = decompressObjectIds(compressedCalendarIds);

        setExcludedCalendarIds(
          difference(map(calendars, "id"), selectedCalendarIds),
        );
      }
    }
  }, [searchParams, calendars]);

  function filterAppointments() {
    let contextAppointments: Appointment[] | undefined;

    if (groupSlug) {
      contextAppointments = filter(appointments, (appointment: Appointment) => {
        const groupAppointmentsCalendarIds = intersection(
          map(contextCalendars, "id"),
          appointment.calendar_ids,
        );

        return (
          groupAppointmentsCalendarIds.length >
          intersection(groupAppointmentsCalendarIds, excludedCalendarIds).length
        );
      });
    } else {
      contextAppointments = filter(appointments, (appointment) => {
        const calendar_ids = (appointment.calendar_ids || []).concat(
          map(appointment.unknown_calendars, "id"),
        );
        return (
          calendar_ids.length >
          intersection(excludedCalendarIds, calendar_ids).length
        );
      });
    }

    return orderBy(contextAppointments, "date.start", "asc");
  }

  const filteredAppointments = useMemo(filterAppointments, [
    appointments,
    groupSlug,
    excludedCalendarIds,
    calendars,
    contextCalendars,
  ]);

  function onSetDate(date: string) {
    sessionStorage.setItem("appointmentsDate", date);
    setDate(date);
  }

  const appointmentsByDate = collectAppointmentsByDate(
    filteredAppointments,
    date,
  );

  function toggleExcludedCalendarIds(calendarId: string) {
    if (calendarId === "all") {
      if (isEmpty(excludedCalendarIds)) {
        setExcludedCalendarIds(map(calendars, "id"));
      } else {
        setExcludedCalendarIds([]);
      }
      selectedCalendarsToParams(null);
    } else {
      let newExcludedIds: string[] = [];

      if (includes(excludedCalendarIds, calendarId)) {
        newExcludedIds = filter(
          excludedCalendarIds,
          (calId: string) => calId !== calendarId,
        );
      } else {
        newExcludedIds = [...excludedCalendarIds, calendarId];
      }

      if (!isEmpty(newExcludedIds)) {
        selectedCalendarsToParams(
          difference(map(calendars, "id"), newExcludedIds),
        );
      } else {
        setExcludedCalendarIds([]);
        selectedCalendarsToParams(null);
      }
    }
  }

  function selectedCalendarsToParams(selectedCalendarIds: string[] | null) {
    const currentUrl = window.location.href;
    const urlWithoutParams = currentUrl.split("?")[0];

    if (selectedCalendarIds && !isEmpty(selectedCalendarIds)) {
      const compressed = compressObjectIds(selectedCalendarIds);
      const calendarUrl = `${urlWithoutParams}?${stringify({
        calendarIds: compressed,
      })}`;
      history.replaceState(null, "", calendarUrl);
    } else {
      history.replaceState(null, "", urlWithoutParams);
    }
  }

  return (
    <>
      <ContextPageTitle
        groupSlug={groupSlug}
        title={
          participating
            ? I18n.t("calendars.participating_appointments")
            : I18n.t("calendars.all_appointments")
        }
      />
      {groupSlug && <GroupNavigation groupSlug={groupSlug} />}
      {!window.isApp && (
        <h2 className="tixxt__subtitle">
          {I18n.t("js.calendars.appointments.subtitle")}
        </h2>
      )}
      <AppointmentToolbar
        isLoading={isLoading}
        view={view}
        setViewType={setViewType}
        date={date}
        setDate={onSetDate}
        calendars={contextCalendars}
        excludedCalendarIds={excludedCalendarIds}
        toggleExcludedCalendarIds={toggleExcludedCalendarIds}
        scrollToToday={scrollToToday}
        canCreate={
          groupSlug
            ? group?.can.create_appointments
            : network.can.create_appointments
        }
      />
      {view === "sheet" ? (
        <CalendarSheet
          appointmentsByDate={appointmentsByDate}
          calendars={calendars}
        />
      ) : (
        <AppointmentsList
          appointmentsByDate={appointmentsByDate}
          date={date}
          todayRef={todayRef}
          calendars={calendars}
        />
      )}
    </>
  );
}
